import {Nav} from '@components/faqs/StyledFAQ';
import {Box, CircularProgress, Grid, Modal, Typography} from '@mui/material';
import {styled, useTheme} from '@mui/material/styles';
import axios from 'axios';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';

import {RootState} from '@/config/store';
import StatusTextGenecic from '@/components/UI/atoms/buttons/StatusTextGeneric';

export const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderCell = styled(Typography)`
    font-size: 9.5px;
    font-weight: 700;
    padding: 8px 6px;
`;

const Cell = styled(Typography)`
    font-size: 9.5px;
    padding: 8px 6px;
    word-break: break-word;
`;

interface IExpenseAccountMessage {
    id: string;
    expenseAccountFolio: string;
    /** `null` en los errores de una carga que no llegó a crear Cuenta de Gastos (VCG-13). */
    version: number | null;
    message: string;
    invoiceReference: string | null;
    invoiceSerie: string | null;
    invoiceFolio: string | null;
    supplierTaxId: string | null;
    supplierName: string | null;
    type: string;
    origin: string;
    result: string | null;
    author: string | null;
    createdAt: string;
}

interface IProps {
    isModalOpen: boolean;
    closeModal: () => void;
    /** Renglón del listado. De aquí salen el código de agente y el pedimento de la ruta. */
    agent: {customsAgentCode: string; request: string; folio?: string} | null;
}

/**
 * Modal "Ver mensajes" de la Cuenta de Gastos (HU 1, criterio 5).
 *
 * Trae sus propios datos con estado local en vez de pasar por el store: el slice
 * `orderssimply` lo comparten varias pantallas y modales, y este modal no necesita que
 * nadie más vea sus mensajes.
 */
const ModalExpenseAccountMessages: React.FC<IProps> = ({isModalOpen, closeModal, agent}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const token = useSelector((state: RootState) => state.logins.accessToken);
    const [messages, setMessages] = useState<IExpenseAccountMessage[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    const fetchMessages = useCallback(async () => {
        if (!agent) return;
        setLoading(true);
        setFailed(false);
        try {
            const {data} = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/v2/customs-agents/${agent.customsAgentCode}` +
                    `/custom-request/${agent.request}/messages`,
                {headers: {Accept: 'application/json', Authorization: 'Bearer ' + token}}
            );
            setMessages(data?.data ?? []);
        } catch (error) {
            // Se distingue "falló la consulta" de "no hay mensajes": son cosas distintas
            // para el Agente Aduanal.
            setFailed(true);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    }, [agent, token]);

    useEffect(() => {
        if (isModalOpen) fetchMessages();
    }, [isModalOpen, fetchMessages]);

    /**
     * Cuando el mensaje no corresponde a una factura concreta, la HU pide mostrar
     * literalmente "Mensaje general de la Cuenta de Gastos".
     */
    const invoiceLabel = (message: IExpenseAccountMessage) =>
        message.invoiceReference ?? t('poagents.messages.general_message');

    const supplierLabel = (message: IExpenseAccountMessage) =>
        message.supplierName ? `${message.supplierName} (${message.supplierTaxId})` : '-';

    return (
        <StyledModal open={isModalOpen} onClose={closeModal}>
            <Box
                sx={{
                    background: '#FFF',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '10px',
                    width: '90vw',
                    maxHeight: '80vh',
                }}
            >
                <Nav>
                    <Typography
                        sx={{
                            fontSize: '18px',
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 700,
                            color: 'white',
                            marginLeft: '10px',
                        }}
                    >
                        {t('poagents.messages.title')}
                        {agent?.folio ? ` · ${agent.folio}` : ''}
                    </Typography>
                </Nav>

                <Box sx={{padding: '16px', overflowY: 'auto'}}>
                    {loading && (
                        <Box sx={{display: 'flex', justifyContent: 'center', padding: '30px'}}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!loading && failed && (
                        <Typography sx={{fontSize: '12px', padding: '20px', textAlign: 'center'}}>
                            {t('poagents.messages.load_error')}
                        </Typography>
                    )}

                    {!loading && !failed && messages.length === 0 && (
                        <Typography sx={{fontSize: '12px', padding: '20px', textAlign: 'center'}}>
                            {t('poagents.messages.empty')}
                        </Typography>
                    )}

                    {!loading && !failed && messages.length > 0 && (
                        <>
                            <Grid container sx={{borderBottom: '1px solid #E5E5E5'}}>
                                <Grid item xs={3}>
                                    <HeaderCell>
                                        {t('poagents.messages.headers.message')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={2}>
                                    <HeaderCell>
                                        {t('poagents.messages.headers.invoice')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={2}>
                                    <HeaderCell>
                                        {t('poagents.messages.headers.supplier')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>{t('poagents.messages.headers.type')}</HeaderCell>
                                </Grid>
                                <Grid item xs={2}>
                                    <HeaderCell>{t('poagents.messages.headers.origin')}</HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>
                                        {t('poagents.messages.headers.version')}
                                    </HeaderCell>
                                </Grid>
                                <Grid item xs={1}>
                                    <HeaderCell>{t('poagents.messages.headers.date')}</HeaderCell>
                                </Grid>
                            </Grid>

                            {messages.map((message) => (
                                <Grid
                                    container
                                    key={message.id}
                                    sx={{borderBottom: '1px solid #F4F4F4'}}
                                >
                                    <Grid item xs={3}>
                                        <Cell>{message.message}</Cell>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Cell
                                            sx={{
                                                fontStyle: message.invoiceReference
                                                    ? 'normal'
                                                    : 'italic',
                                            }}
                                        >
                                            {invoiceLabel(message)}
                                        </Cell>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Cell>{supplierLabel(message)}</Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Cell>
                                            <StatusTextGenecic
                                                status={message.type}
                                                text={t(`poagents.messages.types.${message.type}`)}
                                            />
                                        </Cell>
                                    </Grid>
                                    <Grid item xs={2}>
                                        <Cell>
                                            {t(`poagents.messages.origins.${message.origin}`)}
                                        </Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        {/* Sin versión son errores de una carga que no
                                            llegó a crear Cuenta de Gastos (VCG-13). */}
                                        <Cell>{message.version ?? '—'}</Cell>
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Cell>
                                            {new Date(message.createdAt).toLocaleString('es-MX')}
                                        </Cell>
                                    </Grid>
                                </Grid>
                            ))}
                        </>
                    )}
                </Box>
            </Box>
        </StyledModal>
    );
};

export default ModalExpenseAccountMessages;
